import { Avatar } from "../Avatar";
import { formatRelative } from "../../lib/formatRelative";
import type { Contact } from "../../types";

interface Props {
  contact: Contact;
}

function QuickActionChip({
  href,
  icon,
  label,
}: {
  href: string;
  icon: string;
  label: string;
}) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noreferrer" : undefined}
      className="pill"
      style={{ textDecoration: "none" }}
    >
      <span aria-hidden>{icon}</span>
      <span>{label}</span>
    </a>
  );
}

export function ContactHero({ contact }: Props) {
  const personal = contact.data.personal;
  const professional = contact.data.professional ?? {};
  const metadata = contact.data.metadata ?? {};
  const acquisition = contact.data.acquisition ?? null;
  const name = `${personal.first_name} ${personal.last_name}`.trim();

  const nameClass =
    "font-heading font-bold text-2xl text-ink leading-tight";

  return (
    <div className="panel">
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        <Avatar firstName={personal.first_name} lastName={personal.last_name} />
        <div className="flex-1 min-w-0">
          <div className="label mb-1">Profile</div>
          <h1 className={nameClass}>{name || "Unnamed contact"}</h1>
          <p className="text-ink-low mt-1">
            {professional.job_title || "—"}
            {professional.department ? ` · ${professional.department}` : ""}
          </p>
          {professional.linkedin_headline ? (
            <p className="text-xs text-ink-low mt-1">
              {professional.linkedin_headline}
            </p>
          ) : null}
          {professional.company_name || professional.company_id ? (
            <p className="text-xs text-ink-low mt-1">
              Company:{" "}
              <span className="pill pill--muted">
                {professional.company_name || professional.company_id}
              </span>
            </p>
          ) : null}

          {/* quick actions */}
          <div className="flex flex-wrap gap-2 mt-4">
            {personal.email ? (
              <QuickActionChip
                href={`mailto:${personal.email}`}
                icon="✉️"
                label={personal.email}
              />
            ) : null}
            {personal.phone ? (
              <QuickActionChip
                href={`tel:${personal.phone}`}
                icon="📞"
                label={personal.phone}
              />
            ) : null}
            {personal.linkedin_url ? (
              <QuickActionChip
                href={personal.linkedin_url}
                icon="🔗"
                label="LinkedIn"
              />
            ) : null}
          </div>

          {/* meta strip */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-ink-low">
            {acquisition?.source ? (
              <span>
                Acquired via <strong>{acquisition.source}</strong>
                {acquisition.acquired_at
                  ? ` · ${acquisition.acquired_at.slice(0, 10)}`
                  : ""}
              </span>
            ) : null}
            <span>
              Last contact:{" "}
              <strong>{formatRelative(metadata.last_contact_at)}</strong>
            </span>
            {contact.created_at ? (
              <span>Created {contact.created_at.slice(0, 10)}</span>
            ) : null}
            {contact.updated_at ? (
              <span>Updated {formatRelative(contact.updated_at)}</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
